import { useEffect, useRef, useState } from "react";

/* Smoothly-filling, determinate progress for long LLM jobs (scan / optimize).
   Anchored to the real backend step transitions, with time-based creep inside
   each band so the bar always keeps moving (never stalls, never jumps back).

   Usage:
     const pct = useJobProgress({ active, phase: "optimize", step: serverStep, done });
   - phase: "scan" | "optimize"
   - step:  raw backend step key (optimize: optimizing/scoring/generating/email/cover_letter)
   - done:  set true when the job completes → snaps to 100. */

const BANDS = {
  scan: {
    "":       [6, 90],
    scanning: [6, 90],
  },
  optimize: {
    "":            [6, 58],
    optimizing:    [6, 58],
    scoring:       [58, 76],
    generating:    [76, 90],
    email:         [90, 95],
    cover_letter:  [95, 98],
  },
};

export function useJobProgress({ active, phase = "optimize", step = "", done = false }) {
  const [pct, setPct] = useState(0);
  const pctRef = useRef(0);
  const wasActive = useRef(false);

  // Reset to a small value on the rising edge of a new run.
  useEffect(() => {
    if (active && !wasActive.current) {
      pctRef.current = 4;
      setPct(4);
    }
    wasActive.current = active;
  }, [active]);

  useEffect(() => {
    if (!active && !done) return;
    const bands = BANDS[phase] || BANDS.optimize;
    const id = setInterval(() => {
      const ceiling = done ? 100 : (bands[step] || bands[""] || [6, 90])[1];
      const k = done ? 0.3 : 0.04;
      let next = pctRef.current + (ceiling - pctRef.current) * k;
      // Always nudge forward a hair so it visibly keeps filling within a band.
      if (!done && next < ceiling - 0.4) next += 0.12;
      if (!done && next > 99.2) next = 99.2;
      if (done && next > 99.7) next = 100;
      if (next < pctRef.current) next = pctRef.current; // never go backward
      pctRef.current = next;
      setPct(next);
    }, 220);
    return () => clearInterval(id);
  }, [active, phase, step, done]);

  return Math.min(100, Math.round(pct));
}

export default useJobProgress;
